/**
 * @fileoverview Hook for managing navigation drawer expansion state with
 * transition timing. Consolidates drawer state logic from AppLayout.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface NavigationDrawerState {
  isDesktopExpanded: boolean;
  isMobileExpanded: boolean;
  isFullyExpanded: boolean;
  isExpanded: boolean;
}

interface NavigationDrawerActions {
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
  closeMobile: () => void;
}

export interface UseNavigationDrawerStateOptions {
  defaultCollapsed?: boolean;
  disableCollapsible?: boolean;
  appWindow?: Window;
}

export interface UseNavigationDrawerStateReturn extends NavigationDrawerState, NavigationDrawerActions {
  isOverSmViewport: boolean;
  isOverMdViewport: boolean;
  hasTransitions: boolean;
  isDesktopCollapsed: boolean;
  isMobileCollapsed: boolean;
}

/**
 * Manages navigation drawer expansion state with responsive breakpoints and
 * transition timing for smooth animations.
 */
export function useNavigationDrawerState(
  options: UseNavigationDrawerStateOptions = {},
): UseNavigationDrawerStateReturn {
  const { defaultCollapsed = false, disableCollapsible = false, appWindow } = options;

  const theme = useTheme();

  // Responsive breakpoints
  const isOverSmViewport = useMediaQuery(
    theme.breakpoints.up('sm'),
    appWindow && { matchMedia: appWindow.matchMedia },
  );
  const isOverMdViewport = useMediaQuery(
    theme.breakpoints.up('md'),
    appWindow && { matchMedia: appWindow.matchMedia },
  );

  // Expansion state for desktop and mobile
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(!defaultCollapsed);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  // Transition completion state
  const [isFullyExpanded, setIsFullyExpanded] = useState(!defaultCollapsed);

  // Computed expanded state based on viewport
  const isExpanded = isOverMdViewport ? isDesktopExpanded : isMobileExpanded;

  // Whether transitions should be applied
  const hasTransitions = isOverSmViewport && (!disableCollapsible || isOverMdViewport);

  // Collapsed state for desktop and mobile (tablet)
  const isDesktopCollapsed = !disableCollapsible && !isDesktopExpanded;
  const isMobileCollapsed = !disableCollapsible && !isMobileExpanded;

  // Set expanded state based on current viewport
  const setExpanded = useCallback(
    (newExpanded: boolean) => {
      if (isOverMdViewport) {
        setIsDesktopExpanded(newExpanded);
      } else {
        setIsMobileExpanded(newExpanded);
      }
    },
    [isOverMdViewport],
  );

  const toggleExpanded = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded, setExpanded]);

  const closeMobile = useCallback(() => {
    setIsMobileExpanded(false);
  }, []);

  // Handle expansion transition completion
  useEffect(() => {
    if (isExpanded) {
      const timeout = setTimeout(() => {
        setIsFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(timeout);
    }

    setIsFullyExpanded(false);
    return undefined;
  }, [isExpanded, theme.transitions.duration.enteringScreen]);

  return {
    // State
    isDesktopExpanded,
    isMobileExpanded,
    isFullyExpanded,
    isExpanded,
    isOverSmViewport,
    isOverMdViewport,
    hasTransitions,
    isDesktopCollapsed,
    isMobileCollapsed,
    // Actions
    setExpanded,
    toggleExpanded,
    closeMobile,
  };
}
